// apps/merchant-v3/src/components/TimezoneSelector.tsx
//
// Phase 6 Wave 1 Task 02 — TopBar timezone selector.
// Phase 6 Wave 2 Task 06 — Now sources the curated timezone list from
// `@/lib/format/datetime` and adds an "Auto-detect" entry that asks
// `Intl.DateTimeFormat().resolvedOptions().timeZone` for the browser's
// resolved zone. Real timezone hot-swap (timestamp re-render) is owned
// by `useDateTimeFormatter()` reading user.timezone Zustand.
//
// 2026-05-29 fix(debug): F5 reload was clobbering the local pick because
// useMe() refetches GET /auth/me on mount and overwrites Zustand with
// server-truth. Now we PATCH /auth/me with the new timezone and
// invalidate the useMe cache so the round-trip is symmetric.
//
// vg-binding: PLAN:task-02
// vg-binding: PLAN:task-06
// vg-binding: P6.D-05
// vg-binding: P6.D-131
// vg-binding: P6.D-221

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@printway/ui';
import { Clock, Check, Crosshair } from 'lucide-react';

import { useUser, useUserStore } from '@/store/user';
import { cn } from '@/lib/cn';
import { useTranslation } from '@/lib/i18n';
import { getTimezoneList, resolveBrowserTimezone } from '@/lib/format/datetime';
import { updateProfile } from '@/services/settingsClient';
import { useInvalidateMe } from '@/lib/auth/useMe';

export function TimezoneSelector(): JSX.Element {
  const user = useUser();
  const setUser = useUserStore((s) => s.setUser);
  const invalidateMe = useInvalidateMe();
  const { t } = useTranslation('common');
  const current = user?.timezone ?? resolveBrowserTimezone();
  const options = getTimezoneList();

  const persistTimezone = (tz: string): void => {
    if (!user) return;
    const prev = user;
    setUser({ ...user, timezone: tz });
    void (async () => {
      try {
        await updateProfile({ timezone: tz });
        await invalidateMe();
      } catch {
        setUser(prev);
      }
    })();
  };

  const handleSelect = (tz: string): void => {
    persistTimezone(tz);
  };

  const handleAutoDetect = (): void => {
    persistTimezone(resolveBrowserTimezone());
  };

  // Show short label in trigger
  const triggerLabel = current.split('/').pop() ?? current;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('topbar.selectTimezone')}
          className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm font-medium text-pw-text-muted hover:bg-pw-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <Clock className="h-4 w-4" aria-hidden />
          <span className="hidden md:inline">{triggerLabel.replace('_', ' ')}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="z-50 min-w-[14rem] rounded-md border border-pw-border bg-pw-card p-1 shadow-md"
      >
        <DropdownMenuItem
          onSelect={handleAutoDetect}
          className={cn(
            'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-pw-text-muted outline-none',
            'focus:bg-pw-surface data-[highlighted]:bg-pw-surface',
          )}
        >
          <Crosshair className="h-3.5 w-3.5 text-brand-500" aria-hidden />
          <span>
            {t('timezone.autoDetect', {
              tz: resolveBrowserTimezone(),
              defaultValue: 'Auto-detect ({{tz}})',
            })}
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-1 h-px bg-pw-surface" />
        {options.map((tz) => (
          <DropdownMenuItem
            key={tz.value}
            onSelect={() => handleSelect(tz.value)}
            className={cn(
              'flex cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-pw-text-muted outline-none',
              'focus:bg-pw-surface data-[highlighted]:bg-pw-surface',
            )}
          >
            <span>{tz.label}</span>
            {current === tz.value ? (
              <Check className="h-3.5 w-3.5 text-brand-500" aria-hidden />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default TimezoneSelector;
